import * as React from "react";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DeletionModal from "../../components/modals/DeletionModal";
import NotificationSnackbar from "../../components/NotificationSnackbar";
import EditUserDataModal from "../../components/modals/EditUserDataModal";
import { getCurrentUser } from "@aws-amplify/auth";
import { uploadData, remove } from "@aws-amplify/storage";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, Divider, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { CGI_BLACK, CGI_RED, languages } from "../../helpers/enums";

export default function Account({
  updateImg,
  profileImageId,
  language = languages.DE,
}) {
  const { t } = useTranslation();

  const [currentAttributes, setCurrentAttributes] = React.useState();
  const [openEdit, setOpenEdit] = React.useState(false);

  React.useEffect(() => {
    getUser();
  }, []);

  async function getUser() {
    const user = await getCurrentUser();
    const { attributes } = user;
    setCurrentAttributes(attributes);
  }

  const textSx = { mt: 1.5, fontSize: 15, ml: 4 };

  const [openNotification, setOpenNotification] = React.useState(false);
  const [notificationMessage, setNotificationMessage] = React.useState("");

  async function uploadImage(e) {
    const file = e.target.files[0];
    try {
      await uploadData({
        key: profileImageId,
        data: file,
        options: { accessLevel: "protected" },
      });
      setNotificationMessage(t("uploadImageSuccess"));
    } catch (error) {
      setNotificationMessage(t("uploadError"));
    }
    setOpenNotification(true);
    updateImg();
  }
  async function removeProfileImg() {
    try {
      await remove({ key: profileImageId });
      setNotificationMessage(t("deleteImageSuccess"));
    } catch {
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
    updateImg();
  }

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const [openDelete, setOpenDelete] = React.useState(false);

  return (
    <>
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            sx={{
              fontSize: 24,
              mt: 5,
              ml: 3,
              userSelect: "none",
              color: CGI_RED,
            }}
          >
            {t("accountManagement")}
          </Typography>
          <Divider
            flexItem
            sx={{
              mt: 1,
              mb: 5,
              color: CGI_RED,
              borderBottom: 2,
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", ml: 3 }}>
            <Divider
              flexItem
              textAlign="left"
              sx={{
                width: 1 / 2,
                mb: 1,
                userSelect: "none",
                mt: 1,
                fontWeight: 500,
                bgcolor: "transparent",
                "&::before, &::after": {
                  borderColor: CGI_BLACK,
                },
                color: CGI_BLACK,
              }}
            >
              {t("enteredInfo")}
            </Divider>
            <Box sx={{ display: "flex", flexDirection: "column", ml: 3 }}>
              <Typography sx={textSx}>
                {`${t("loggedInAs")}: ${currentAttributes?.email}`}
              </Typography>
              <Typography sx={textSx}>
                {`${t("prename")}: ${currentAttributes?.given_name || "-"}`}
              </Typography>
              <Typography sx={textSx}>
                {`${t("surname")}: ${currentAttributes?.family_name || "-"}`}
              </Typography>
              <Typography sx={textSx}>
                {`${t("mobile")}: ${currentAttributes?.phone_number || "-"}`}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setOpenEdit(true)}
              sx={{ width: 275, mt: 5, ml: 2, borderRadius: 10 }}
            >
              {t("editUserData")}
            </Button>
            <Divider flexItem sx={{ my: 1.5, width: 400 }} />
            <Box sx={{ display: "flex", flexDirection: "row", ml: 2 }}>
              <Button
                component="label"
                variant="outlined"
                onChange={uploadImage}
                startIcon={<FileUploadIcon />}
                sx={{
                  borderRadius: 10,
                  textTransform: "none",
                  px: 2,
                  mr: 2,
                  height: 25,
                }}
              >
                {t("uploadImage")}
                <VisuallyHiddenInput type="file" />
              </Button>
              <Button
                variant="contained"
                onClick={() => setOpenDelete(true)}
                startIcon={<DeleteForeverIcon />}
                sx={{
                  borderRadius: 10,
                  textTransform: "none",
                  px: 2,
                  height: 25,
                }}
              >
                {t("deleteCurrentImage")}
              </Button>
            </Box>
          </Box>
        </Box>
        <EditUserDataModal
          open={openEdit}
          setOpen={setOpenEdit}
          setNotificationMessage={setNotificationMessage}
          setOpenNotification={setOpenNotification}
        />
      </Container>
      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
      <DeletionModal
        open={openDelete}
        setOpen={setOpenDelete}
        deleteFunction={removeProfileImg}
        safeDelete={true}
        itemClass="Item"
        id={t("image")}
      />
    </>
  );
}
